import React, {useState, useRef, useMemo, useCallback} from "react";
import {useFrame} from "@react-three/fiber";
import {useXR, Interactive, useXRInputSourceState, XRHitTest, isXRInputSourceState, XRControllerState} from "@react-three/xr";
import * as THREE from "three";
import {Html} from "@react-three/drei";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";
import {store} from "../../../../Store/index.js";
import {HTMLInVR} from "../../../../Utils/ThreeJS/XR/HTMLInVR.js";
import {GetDreamEvents, GetJournalEntries} from "../../../../Server/DB/journalEntries.js";
import {DreamEventsUI} from "../../../Journal/JournalEntry/DreamEventsUI.js";
import {MeID} from "../../../../Server/DB/users.js";
import {JournalEntry, JournalSegment} from "../../../../Server/DB/journalEntries/@JournalEntry.js";
import {LHQInstance} from "../LHQ/Scene.js";
import {RNG_SFC32} from "../../../../Utils/General/RNG/PRNGs/RNG_SFC32.js";
import {Rect, VBounds, Vec3} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {GetCanvasFont, GetTextWidth} from "../../../../Utils/UI/TextMeasurer.js";
import {event} from "react-ga";
import {CreateAccessor, RunInAction} from "mobx-graphlink";
import {HashString} from "../../../../Utils/General/General.js";
import {GetSegmentPath} from "../../../../Store/main/tools/vr.js";
import {Remote_RequestTTS, remoteUserProcessesBridge} from "../../../../Utils/Bridge/Bridge_RemoteUserProcesses.js";
import {GetSelectedEngineConfig} from "../../../../Server/DB/engineConfigs.js";
import {EngineConfig_VR} from "../../../../Server/DB/engineConfigs/@EngineConfig/@EC_VR.js";
import {GetControllerSide_Final} from "../../../../Store/main/settings/vr.js";

const pixelsPerMeter = 200;
export const DreamPanels = observer_mgl_r3((props: {lhq: LHQInstance})=>{
	const {lhq} = props;
	const randomSeed = useMemo(()=>Math.random(), []);
	const config = GetSelectedEngineConfig()?.vr;
	if (config == null) return null;

	const dreamSegmentsWithEvents = GetJournalEntries(MeID(), "desc").SelectMany(entry=>{
		return entry.segments.filter(a=>a.wakeTime == null).map(segment=>{
			const events = GetDreamEvents(segment);

			let eventsToShow = events;
			// if for vr, limit events per dream-panel to (a randomly selected slice of) 10 events (so comparing text<>audio doesn't take too long)
			if (lhq.useVR && eventsToShow.length > config.dreams_events_max) {
				const start_maxI = eventsToShow.length - config.dreams_events_max;
				const rng = new RNG_SFC32({seed_int: segment.id ? HashString(segment.id) : HashString(JSON.stringify(eventsToShow))});
				const selectedStart = Math.floor(rng.GetNextFloat() * (start_maxI + 1));
				eventsToShow = eventsToShow.slice(selectedStart, selectedStart + config.dreams_events_max);
			}

			return {
				journalEntry: entry,
				segment,
				events: eventsToShow,
			};
		});
	}).filter(a=>a.events.length >= config.dreams_events_min);

	const randomPosArea_center = new Vec3(-2, 0, 1);
	const randomPosArea_size = new Vec3(10, 12, 1);
	const randomPosArea_base = randomPosArea_center.Minus(randomPosArea_size.Times(.5));
	const rng = new RNG_SFC32({seed_float: randomSeed}); // todo: have this seed changed each time a sleep-cycle is "solved"

	const dreamSegmentsToShow = dreamSegmentsWithEvents.slice(0, config.dreams_count);
	const dreamSegmentsToShow_sizeInfos = dreamSegmentsToShow.map(a=>GetDreamPanelSizeInfo(a.segment, a.events));
	const dreamSegmentsToShow_placements = [] as VBounds[];
	for (let i = 0; i < dreamSegmentsToShow.length; i++) {
		const entry = dreamSegmentsToShow[i];
		const sizeInfo = dreamSegmentsToShow_sizeInfos[i];
		//const rng = new RNG_SFC32({seed_int: entry.segment.id ? HashString(entry.segment.id) : i});

		const randomCenter = ()=>new Vec3(randomPosArea_base.x + (rng.GetNextFloat() * randomPosArea_size.x), randomPosArea_base.y + (rng.GetNextFloat() * randomPosArea_size.y), randomPosArea_base.z + (rng.GetNextFloat() * randomPosArea_size.z));
		const size = new Vec3(sizeInfo.longestRowWidth / pixelsPerMeter, sizeInfo.longestRowWidth / pixelsPerMeter, sizeInfo.totalHeight / pixelsPerMeter);
		//let size = new Vec3(sizeInfo.avgRowWidth / pixelsPerMeter, sizeInfo.avgRowWidth / pixelsPerMeter, sizeInfo.totalHeight / pixelsPerMeter);
		const boundsFromCenter = (center: Vec3)=>new VBounds(center.Minus(size.Times(.5)), size);

		let boundsCandidate = boundsFromCenter(randomCenter());
		// only try to find a placement 100 times; if still fails, just use the last one
		for (let i = 0; i < 500 && dreamSegmentsToShow_placements.some(a=>a.Intersects(boundsCandidate)); i++) {
			boundsCandidate = boundsFromCenter(randomCenter());
			if (i >= 499) console.warn("Failed to find non-intersecting placement for dream panel. @lastTry:", boundsCandidate, "@placements:", dreamSegmentsToShow_placements);
		}
		dreamSegmentsToShow_placements.push(boundsCandidate);
	}

	return <>
		{dreamSegmentsToShow.map((entry, index)=>{
			const bounds = dreamSegmentsToShow_placements[index];
			//const rng = new RNG_SFC32({seed_int: entry.segment.id ? HashString(entry.segment.id) : index});
			const rot = new Vec3(0, 0, rng.GetNextFloat() * (Math.PI * 2));

			// if random rotation faces "away from center of room" (0,0,0), rotate it to face towards room-center
			// Get the object's forward direction (negative Z axis)
			const forwardDirection = new THREE.Vector3(0, 0, -1).applyEuler(rot.To3JRot());
			// Calculate direction to origin
			const toOrigin = new THREE.Vector3(0, 0, 0).sub(bounds.Center.To3JPos()).normalize();
			// Use dot product to determine if facing toward origin
			const dotProduct = forwardDirection.dot(toOrigin);
			const isFacingOrigin = !(dotProduct > 0); // we have to reverse this for some reason (maybe UVs for plane are backward; maybe "forwardDirection" is reversed; not sure)
			if (!isFacingOrigin) {
				rot.z += Math.PI;
			}

			return <DreamPanel key={entry.segment.id ?? index} {...{lhq, config}} journalEntry={entry.journalEntry} segment={entry.segment} events={entry.events} position={bounds.Center.To3JPos()} rotation={[...rot.To3JPos().toArray()]}/>;
		})}
	</>;
});

export const GetDreamPanelSizeInfo = CreateAccessor((segment: JournalSegment, events: string[])=>{
	const containerHPadding = 5 + 5; // left + right
	const rowWidths = events.map(event=>{
		const eventHPadding = 3 + 3; // left + right
		const totalHPadding = containerHPadding + eventHPadding;
		const textWidth = GetTextWidth(event, GetCanvasFont());
		return textWidth + totalHPadding;
	});
	const longestRowWidth = rowWidths.Max();
	const avgRowWidth = rowWidths.Average();

	const containerVPadding = 5 + 5; // left + right
	const rowHeight = 20;
	const rowGap = 3;
	const totalHeight = containerVPadding + ((rowGap + rowHeight) * events.length) - rowGap; // -rowGap, since first entry doesn't have a gap/margin-top

	return {longestRowWidth, avgRowWidth, totalHeight};
});

export const DreamPanel = observer_mgl_r3((props: {
	lhq: LHQInstance,
	config: EngineConfig_VR,
	journalEntry: JournalEntry,
	segment: JournalSegment,
	events: string[],
	position: THREE.Vector3 | [x: number, y: number, z: number],
	rotation: THREE.Euler | [x: number, y: number, z: number],
})=>{
	const {lhq, config, journalEntry, segment, events, position, rotation} = props;
	const uiState = store.main.tools.vr;

	const htmlRef = useRef<THREE.Mesh | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [lastPosition, setLastPosition] = useState([0, 0, 0]);
	//const [interactionMode, setInteractionMode] = useState('drag');

	const rightController = lhq.useVR ? useXRInputSourceState("controller", GetControllerSide_Final("right")) : null;
	const rightControllerObj = rightController?.object;

	console.log("IsDragging_render:", isDragging);
	useFrame((state, delta)=>{
		if (isDragging) console.log("IsDragging_Frame:", isDragging, htmlRef.current, rightControllerObj);
		if (isDragging && htmlRef.current && rightControllerObj) {
			const dx = rightControllerObj.position.x - lastPosition[0];
			const dy = rightControllerObj.position.y - lastPosition[1];
			const dz = rightControllerObj.position.z - lastPosition[2];

			const pos = htmlRef.current.position;
			htmlRef.current.position.set(pos.x + dx, pos.y + dy, pos.z + dz);

			setLastPosition([rightControllerObj.position.x, rightControllerObj.position.y, rightControllerObj.position.z]);
		}
	});

	const segmentPath = GetSegmentPath(journalEntry, segment);
	//const [highlightedEventIndexes, setHighlightedEventIndexes] = useState<number[]>([]);
	const [activeEventIndex, setActiveEventIndex] = useState<number>(-1);

	const {longestRowWidth, totalHeight} = GetDreamPanelSizeInfo(segment, events);
	const vrSettings = store.main.settings.vr;
	return (
		<group ref={htmlRef} position={position} rotation={rotation}
			onPointerDown={e=>{
				const pointerState = e["pointerState"] as XRControllerState | null;
				if (uiState.right_interactionMode != "interact" || !isXRInputSourceState(pointerState) || pointerState.inputSource.handedness != GetControllerSide_Final("right")) return;
				const controllerObj = pointerState.object;
				if (controllerObj == null) return;
				setIsDragging(true);
				setLastPosition([controllerObj.position.x, controllerObj.position.y, controllerObj.position.z]);
			}}
			onPointerUp={e=>{
				const pointerState = e["pointerState"] as XRControllerState | null;
				if (uiState.right_interactionMode != "interact" || !isXRInputSourceState(pointerState) || pointerState.inputSource.handedness != GetControllerSide_Final("right")) return;
				setIsDragging(false);
			}}
		>
			<HTMLInVR vrActive={lhq.useVR} width={longestRowWidth / pixelsPerMeter} height={totalHeight / pixelsPerMeter} pixelsPerMeter={pixelsPerMeter}
				position={[0, 0, 0]} rotation={[0, 0, 0]} canvasPixelRatio={3} backgroundColor="transparent"
				onPointerDown={()=>{
					if (uiState.right_interactionMode != "interact") return;
					RunInAction("LHQ_DreamPanel_onPointerDown", ()=>uiState.activeDreamPanel_segmentPath = segmentPath);
					if (activeEventIndex == -1) {
						setActiveEventIndex(0);
						Remote_RequestTTS(events[0] ?? "", vrSettings.dreamPanel_ttsVolumeMultiplier, vrSettings.dreamPanel_ttsSpeedMultiplier);
					}
				}}
				onStickDirectionalStateChanged={useCallback((side, direction, state)=>{
					if (uiState.right_interactionMode != "interact" || uiState.activeDreamPanel_segmentPath != segmentPath) return;
					if (side == GetControllerSide_Final("right") && state == "on") {
						const oldActiveIndex = activeEventIndex;
						const newActiveIndex = direction == "up"
							? (activeEventIndex - 1).KeepAtLeast(-1)
							: (activeEventIndex + 1).KeepAtMost(events.length).KeepAtMost(config.dreams_events_max);
						if (newActiveIndex != oldActiveIndex) {
							setActiveEventIndex(newActiveIndex);
							Remote_RequestTTS(events[newActiveIndex] ?? "", vrSettings.dreamPanel_ttsVolumeMultiplier, vrSettings.dreamPanel_ttsSpeedMultiplier);
						}
					}
				}, [activeEventIndex, segmentPath])}
				//redrawDeps={highlightedEventIndexes}
				//test1={uiState.activeDreamPanel_segmentPath == segmentPath}
			>
				<DreamEventsUI segment={segment} events={events} activeInVR={uiState.activeDreamPanel_segmentPath == segmentPath} activeEventIndex={activeEventIndex} forVR={true} style={{
					width: "100%", height: "100%",
					//width: 200, height: 400,
				}}/>
			</HTMLInVR>
		</group>
	);
});